import { isValidLocalPart } from "@catmail/shared";
import { useEffect, useState } from "react";

import { FormDialog, TextField } from "@/components";

/**
 * 주소 하나 더 신청하기.
 *
 * 이 앱에서 주소를 늘리는 것은 **행 하나 넣는 것**이 전부다. 도메인 전체가
 * catch-all로 들어오고 받을지 말지는 목록이 정한다(architecture.md §1). 그래서
 * `netflix@`처럼 사이트마다 주소를 따로 파도 비용이 없고, 어디가 주소를
 * 흘렸는지 바로 알 수 있다 — 그 쓰임새를 설명에 적어두는 이유다.
 *
 * 다만 **누르는 순간 열리지는 않는다.** 최고 관리자가 받아줘야 그 주소로 메일이
 * 들어온다. 단추 이름을 "만들기"로 두면 바로 쓸 수 있다고 읽혀서, 열리지도 않은
 * 주소를 어디에 적어두게 된다. 그 메일은 엣지에서 거절당해 사라진다.
 */

type AddAddressDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `@` 뒤에 붙는 것. 하나뿐이라 고르지 않고 보여주기만 한다 */
  mailDomain: string;
  pending?: boolean;
  /** 서버가 거절한 이유. "이미 쓰이고 있는 주소입니다" 같은 것 */
  error?: string | null;
  onSubmit: (localPart: string) => void;
};

export function AddAddressDialog({
  open,
  onOpenChange,
  mailDomain,
  pending = false,
  error,
  onSubmit,
}: AddAddressDialogProps) {
  const [localPart, setLocalPart] = useState("");

  useEffect(() => {
    if (!open) setLocalPart("");
  }, [open]);

  const trimmed = localPart.trim().toLowerCase();
  // 빈 칸에까지 빨간 글씨를 띄우지 않는다. 아직 아무것도 안 한 사람을 나무라는 꼴이다.
  const malformed = trimmed.length > 0 && !isValidLocalPart(trimmed);

  const hint = malformed
    ? "영문 소문자와 숫자로 시작하고, . _ - 만 쓸 수 있어요"
    : (error ?? null);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="주소 늘리기"
      description="쓰는 곳마다 주소를 따로 두면, 어디서 주소가 샜는지 바로 알 수 있어요."
      // "만들기"가 아니다. 여기서 끝나지 않고 사람이 한 번 더 본다.
      submitLabel="신청하기"
      pendingLabel="신청하는 중…"
      pending={pending}
      canSubmit={isValidLocalPart(trimmed)}
      hint={
        hint ? (
          <p className="text-danger text-[13px]">{hint}</p>
        ) : (
          <p className="text-muted-3 text-[13px]">
            신청하면 받아들여질 때까지 기다려요. 그 전에는 이 주소로 온 메일이
            들어오지 않아요.
          </p>
        )
      }
      onSubmit={() => onSubmit(trimmed)}
    >
      <TextField
        label="새 주소"
        placeholder="netflix"
        autoFocus
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        // 도메인은 서버가 붙인다. 하나뿐이라 칠 것이 없다.
        trailing={<span className="text-muted-2 text-[15px]">@{mailDomain}</span>}
        value={localPart}
        onChange={(event) => setLocalPart(event.target.value)}
      />
    </FormDialog>
  );
}
